import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  getBulkUploadStatusAPI,
  getBulkUploadFailedItemsAPI,
} from "../../../../../api/index";
import "./BulkUploadProgressModal.css";

const POLL_INTERVAL_MS = 3000;

const BulkUploadProgressModal = ({ isOpen, onClose, jobId, rejectedRows = [] }) => {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !jobId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const response = await getBulkUploadStatusAPI(jobId);
        if (!cancelled) {
          setStatus(response.data);
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.msg || "진행상황을 불러오지 못했습니다.",
          );
        }
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
    };
  }, [isOpen, jobId]);

  const handleDownloadFailed = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      const response = await getBulkUploadFailedItemsAPI(jobId);
      const failedItems = response.data || [];

      const rows = [
        ...rejectedRows.map((r) => ({ ...r.rawRow, 실패사유: r.reason })),
        ...failedItems.map((item) => ({
          ...item.rawRow,
          실패사유: item.failReason,
        })),
      ];

      if (rows.length === 0) {
        alert("실패한 행이 없습니다.");
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "실패 목록");
      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `올줌_대량등록_실패목록_${today}.xlsx`);
    } catch (err) {
      alert(
        `실패 목록을 가져오지 못했습니다: ${
          err.response?.data?.msg || err.message
        }`,
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  const total = status?.totalCount ?? 0;
  const success = status?.successCount ?? 0;
  const fail = status?.failCount ?? 0;
  const pending = status?.pendingCount ?? 0;
  const rejected = status?.rejectedCount ?? rejectedRows.length;
  const isDone = status?.status === "DONE";

  return (
    <div className="bulkuploadmodal-overlay" onClick={onClose}>
      <div
        className="bulkuploadmodal-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bulkuploadmodal-header">
          <h2>대량등록 진행상황</h2>
          <button className="bulkuploadmodal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {error && <p className="bulkuploadmodal-error">{error}</p>}

        <div className="bulkuploadmodal-summary">
          <span className="bulkuploadmodal-status-label">
            {isDone ? "완료" : "처리 중..."}
          </span>
        </div>

        <div className="bulkuploadmodal-counts">
          <div className="bulkuploadmodal-count-item">
            <span className="bulkuploadmodal-count-value">{total}</span>
            <span className="bulkuploadmodal-count-label">전체</span>
          </div>
          <div className="bulkuploadmodal-count-item bulkuploadmodal-count-success">
            <span className="bulkuploadmodal-count-value">{success}</span>
            <span className="bulkuploadmodal-count-label">완료</span>
          </div>
          <div className="bulkuploadmodal-count-item">
            <span className="bulkuploadmodal-count-value">{pending}</span>
            <span className="bulkuploadmodal-count-label">대기중</span>
          </div>
          <div className="bulkuploadmodal-count-item bulkuploadmodal-count-fail">
            <span className="bulkuploadmodal-count-value">
              {fail + rejected}
            </span>
            <span className="bulkuploadmodal-count-label">실패</span>
          </div>
        </div>

        <p className="bulkuploadmodal-desc">
          광고 등록은 이미 완료됐고, 상품정보는 뒤에서 채워지는 중입니다.
          이 창을 닫아도 처리는 계속됩니다.
        </p>

        <div className="bulkuploadmodal-actions">
          <button
            className="bulkuploadmodal-download-btn"
            onClick={handleDownloadFailed}
            disabled={downloading || fail + rejected === 0}
          >
            {downloading ? "다운로드 중..." : "실패 목록 다운로드"}
          </button>
          <button className="bulkuploadmodal-close-button" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadProgressModal;
